// @ts-nocheck
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import OrderActionSet, {
    orderActionSetFieldDefinitions,
    OrderActionSetPresentationProps,
} from "@store/Library/Components/Toolkit/Order/OrderActionSet";
import ShareEntityButton, { ShareEntityButtonStyles } from "@store/Overrides/Components/ShareEntityButton";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import React, { useContext } from "react";

/* Store Custom Code - Start */
import translate from "@insite/client-framework/Translate";
import Download from "@store/Library/Components/Toolkit/Icons/Download";
import useDownloadInvoice from "@store/Framework/Common/Hooks/useDownloadInvoice";
/* Store Custom Code - End */

const enum fields {
    showPrint = "showPrint",
    showShareViaEmail = "showShareViaEmail",
    // Store Custom Code
    showDownload = "showDownload",
}

export interface InvoiceDetailsActionsPresentationProps extends ExpandStyles<InvoiceDetailsActionsStyles> {}

export interface InvoiceDetailsActionsProps extends InvoiceDetailsActionsPresentationProps, WidgetProps {
    fields: {
        [fields.showPrint]?: boolean;
        [fields.showShareViaEmail]?: boolean;
        // Store Custom Code
        [fields.showDownload]?: boolean;
    };
}

export interface InvoiceDetailsActionsStyles {
    actionSet?: OrderActionSetPresentationProps;
    shareEntityStyles?: ShareEntityButtonStyles;
    printButton?: ButtonPresentationProps;
    printLink?: LinkPresentationProps;
    // Store Custom Code
    downloadButton?: ButtonPresentationProps;
    downloadLink?: LinkPresentationProps;
}

export const invoiceDetailsActionsStyles: InvoiceDetailsActionsStyles = {
    /* Store Custom Code - Start */
    downloadLink: { icon: { iconProps: { src: Download } } },
    /* Store Custom Code - End */
};

const InvoiceDetailsActions = ({
    fields: widgetFields,
    extendedStyles,
    overridedStyles,
}: InvoiceDetailsActionsProps) => {
    const styles = useExpandProps(invoiceDetailsActionsStyles, extendedStyles, overridedStyles);
    const { value: invoice } = useContext(InvoiceStateContext);
    const { downloadInvoice, isLoadingInvoice } = useDownloadInvoice(); // Store Custom Code

    if (!invoice) {
        return null;
    }

    return (
        <OrderActionSet
            {...styles.actionSet}
            fields={widgetFields}
            actions={[
                {
                    preset: "print",
                    buttonStyles: styles.printButton,
                    linkStyles: styles.printLink,
                    field: fields.showPrint,
                },
                {
                    button: (
                        <ShareEntityButton
                            key="share"
                            entityId={invoice.invoiceNumber}
                            entityName="Invoice"
                            extendedStyles={styles.shareEntityStyles}
                        />
                    ),
                    link: (
                        <ShareEntityButton
                            key="share"
                            entityId={invoice.invoiceNumber}
                            entityName="Invoice"
                            extendedStyles={styles.shareEntityStyles}
                            variant="clickable"
                        />
                    ),
                    field: fields.showShareViaEmail,
                },
                /* Store Custom Code - Start */
                {
                    key: "download-invoice",
                    label: translate("Download Invoice"),
                    onClick: () => downloadInvoice({ invoiceNumber: invoice.invoiceNumber }),
                    isLoading: isLoadingInvoice,
                    buttonStyles: styles.downloadButton,
                    linkStyles: styles.downloadLink,
                    field: fields.showDownload,
                },
                /* Store Custom Code - End */
            ]}
        />
    );
};

const widgetModule: WidgetModule = {
    component: InvoiceDetailsActions,
    definition: {
        group: "Invoice History",
        allowedContexts: ["InvoiceDetailsPage"],
        displayName: "Actions",
        fieldDefinitions: orderActionSetFieldDefinitions([
            { name: fields.showDownload }, // Store Custom Code
            { name: fields.showShareViaEmail, defaultValue: false }, // Store Custom Code
            { name: fields.showPrint },
        ]),
    },
};

export default widgetModule;
